import { useId, useState } from 'react';
import { api } from '../../../../services/api';
import { ModalDecisao, Selo, useDecisao } from '../../../common/interface';
import { GrupoPassageirosDialog } from '../../grupos/GrupoPassageirosDialog';
import { dataDoDia, moeda } from '../../formato';
import { atfStatusInfo } from './atfStatusInfo';
import { useMotivosCancelamento } from './useAcompanhamento';
import type { AtfItem } from './types';

interface AtfCardProps {
  item: AtfItem;
  expandido: boolean;
  onToggle: () => void;
  onMudou: () => void;
}

/** Quantos passageiros aparecem antes do chip de resto (design/ §3.2). */
const PAX_VISIVEIS = 5;

/**
 * Cidade legível a partir do que o backend resolveu.
 *
 * O fallback é o índice cru do DER: se a resolução falhar, é melhor mostrar
 * "684" do que uma linha vazia — mas isso é o caso degradado, não o normal
 * (a resolução alcança 826 de 826 ATFs hoje).
 */
function cidade(nome: string | undefined, uf: string | undefined, indice: number): string {
  if (!nome) return String(indice);
  return uf ? `${nome}/${uf}` : nome;
}

/** `2026-08-30T07:41:00` → `30/08 · 07:41`. Sem `new Date`: o valor já é BRT. */
function dataHoraCurta(iso: string | undefined): string {
  if (!iso) return '—';
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
  return m ? `${m[3]}/${m[2]} · ${m[4]}:${m[5]}` : iso;
}

/**
 * Card de uma ATF — layout do mockup de 03/09 (specs/0035 design/ §3).
 *
 * O slot livre do cabeçalho é do **grupo de passageiros**, não do valor: o
 * valor vive no detalhe, com a composição fiscal abaixo. O grid do expandido
 * mostra o que identifica o documento (número, verificador, CRC, finalizado
 * em) no lugar de itinerário e quilometragem, que o cooperado já sabe.
 *
 * Desde a 0094 o cabeçalho é um botão (abria só com o mouse), e as três ações
 * dizem a consequência antes, com o erro dentro da janela: ele aparecia no
 * cartão, atrás do véu.
 */
export function AtfCard({ item, expandido, onToggle, onMudou }: AtfCardProps) {
  const idDetalhe = useId();
  const info = atfStatusInfo(item.status, item.chPlc);
  const { janela, confirmar } = useDecisao();
  const [dialogExcluir, setDialogExcluir] = useState(false);
  const [dialogCancelar, setDialogCancelar] = useState(false);
  const [erroJanela, setErroJanela] = useState<string | null>(null);
  const [erroAcao, setErroAcao] = useState<string | null>(null);
  const [avisoAcao, setAvisoAcao] = useState<string | null>(null);
  const [processando, setProcessando] = useState(false);
  const [verTodosPax, setVerTodosPax] = useState(false);
  const [dialogGrupo, setDialogGrupo] = useState(false);
  const motivos = useMotivosCancelamento();

  const temDocFiscal = Boolean(item.numeroDocFiscal);
  const semChavePortal = item.status === 'EMITIDA' && !item.chPlc;
  const paxVisiveis = verTodosPax ? item.passageiros : item.passageiros.slice(0, PAX_VISIVEIS);
  const paxRestantes = item.passageiros.length - paxVisiveis.length;
  const nomeDaAtf = item.numeroAtf ? `ATF ${item.numeroAtf}` : 'esta ATF';

  function fecharJanelas() {
    if (processando) return;
    setDialogExcluir(false);
    setDialogCancelar(false);
    setErroJanela(null);
  }

  async function excluir() {
    setProcessando(true);
    setErroJanela(null);
    try {
      await api.delete(`/cooperado/atf/${item.id}`);
      setDialogExcluir(false);
      onMudou();
    } catch (e: any) {
      setErroJanela(e?.response?.data?.message || 'Não foi possível excluir a ATF.');
    } finally {
      setProcessando(false);
    }
  }

  async function cancelar(valores: Record<string, string>) {
    const motivo = motivos.find(m => m.rotulo === valores.motivo);
    if (!motivo) return;
    setProcessando(true);
    setErroJanela(null);
    try {
      const r = await api.post(`/cooperado/atf/${item.id}/cancelar`, {
        motivo: motivo.codigo,
        observacao: valores.observacao?.trim() || undefined,
      });
      setDialogCancelar(false);
      // 202: a ATF saiu no DER, e o documento fiscal ficou para a cooperativa
      // tratar. Era lido como sucesso comum, e ninguém sabia do CT-e.
      setAvisoAcao(r.status === 202
        ? 'A ATF foi cancelada no DER. O documento fiscal dela não pôde ser cancelado agora e ficou com a cooperativa, que trata à mão.'
        : 'A ATF foi cancelada no DER.');
      onMudou();
    } catch (e: any) {
      setErroJanela(e?.response?.data?.message || 'Não foi possível cancelar a ATF.');
    } finally {
      setProcessando(false);
    }
  }

  async function reenviar() {
    const sim = await confirmar({
      titulo: `Reenviar ${nomeDaAtf}?`,
      consequencia: 'A ATF volta para a fila do DER, com o mesmo documento fiscal. O sistema procura a ATF no portal antes de emitir; se o portal não responder, ela fica "a verificar", e pode sair duplicada.',
      rotuloConfirmar: 'Reenviar',
    });
    if (!sim) return;
    setProcessando(true);
    setErroAcao(null);
    setAvisoAcao(null);
    try {
      await api.post(`/cooperado/atf/${item.id}/reenviar`);
      setAvisoAcao('A ATF voltou para a fila do DER.');
      onMudou();
    } catch (e: any) {
      setErroAcao(e?.response?.data?.message || 'Não foi possível reenviar a ATF.');
    } finally {
      setProcessando(false);
    }
  }

  return (
    <article className="co-atf">
      {janela}
      <button
        type="button"
        className="co-atf-cabeca"
        aria-expanded={expandido}
        aria-controls={idDetalhe}
        onClick={onToggle}
      >
        <span className="co-atf-numero">
          {item.numeroAtf ? `ATF ${item.numeroAtf}` : 'Aguardando o DER'}
          <Selo papel={info.papel}>{info.rotulo}</Selo>
        </span>

        <span className="co-atf-rota">
          <span className="co-atf-rota-cidades">
            {cidade(item.municipioOrigemNome, item.municipioOrigemUf, item.municipioOrigem)}
            {' → '}
            {cidade(item.municipioDestinoNome, item.municipioDestinoUf, item.municipioDestino)}
          </span>
          <span className="co-atf-rota-apoio">{item.placa} · {dataDoDia(item.dataIda)}</span>
        </span>

        <span className="co-atf-pax">{item.passageiros.length} pax</span>

        {/* O slot do grupo — no mockup é aqui, não o valor. */}
        <span className="co-atf-grupo-slot">
          {item.nomeGrupo ? <span className="co-atf-grupo">{item.nomeGrupo}</span> : <span className="co-apoio">—</span>}
        </span>

        <span className="co-atf-abrir">{expandido ? 'Fechar' : 'Detalhes'}</span>
      </button>

      {expandido && (
        <div id={idDetalhe} className="co-atf-detalhe">
          <dl className="co-atf-grade">
            <ParInfo rotulo="Número" valor={item.numeroAtf || '—'} />
            <ParInfo rotulo="Verificador" valor={item.verificador || '—'} />
            <ParInfo rotulo="CRC" valor={item.crc || '—'} />
            <ParInfo rotulo="Finalizado em" valor={dataHoraCurta(item.finalizadoEm)} />
            <ParInfo
              rotulo="Documento fiscal"
              valor={item.numeroDocFiscal ? `nº ${item.numeroDocFiscal} · série ${item.serieDocFiscal}` : '—'}
            />
            <ParInfo rotulo="Condutor" valor={item.condutorNome} />
            <ParInfo rotulo="Veículo" valor={item.placa} />
            {/* `valores: null` é "sem documento fiscal", não "custou zero". */}
            <ParInfo rotulo="Valor" valor={item.valores ? moeda(item.valores.valorTotal) : '—'} />
          </dl>

          {item.valores && (
            <div className="co-atf-valores">
              <span>Base de cálculo <b>{moeda(item.valores.valorBaseCalculo)}</b></span>
              <span>Alíquota <b>{item.valores.aliquotaIcms}%</b></span>
              <span>ICMS <b>{moeda(item.valores.valorIcms)}</b></span>
              {item.valores.tomadorNome && <span>Contratante <b>{item.valores.tomadorNome}</b></span>}
              {item.valores.componentes.map((c, i) => (
                <span key={i}>{c.nome} <b>{moeda(c.valor)}</b></span>
              ))}
            </div>
          )}

          {item.erroMensagem && (
            // Erro real é neutro; observação do órgão numa ATF emitida é
            // informação boa, e pintá-la de alerta assustaria à toa.
            <div className={`co-nota${item.status === 'EMITIDA' ? ' co-nota-sucesso' : ''}`}>
              <span className="co-rotulo">{item.status === 'EMITIDA' ? 'Aviso do órgão' : 'Motivo'}</span>
              {item.erroMensagem}
            </div>
          )}

          {semChavePortal && (
            <p className="co-nota">
              Esta ATF não tem a chave do portal registrada, então o cancelamento não pode ser feito por aqui.
              Fale com o escritório da cooperativa — eles cancelam pelo portal do órgão.
            </p>
          )}

          <div className="co-nota-livre">
            <div className="co-cabecalho">
              <span className="co-rotulo">Passageiros ({item.passageiros.length})</span>
              {/* "Criar grupo com os dados da ATF" — a lista já foi digitada
                  uma vez; reaproveitá-la é o ponto do recurso. */}
              {item.passageiros.length > 0 && !item.nomeGrupo && (
                <button type="button" className="co-btn co-btn-texto" onClick={() => setDialogGrupo(true)}>
                  Salvar como grupo
                </button>
              )}
            </div>
            {item.passageiros.length === 0 ? (
              <span className="co-apoio">—</span>
            ) : (
              <div className="co-etiquetas">
                {paxVisiveis.map((p, i) => <span key={i} className="co-etiqueta">{p.nome}</span>)}
                {paxRestantes > 0 && (
                  <button type="button" className="co-etiqueta" onClick={() => setVerTodosPax(true)}>
                    +{paxRestantes} passageiro{paxRestantes === 1 ? '' : 's'}
                  </button>
                )}
              </div>
            )}
          </div>

          {erroAcao && <p className="co-erro" role="alert">{erroAcao}</p>}
          {avisoAcao && <p className="co-nota co-nota-sucesso" role="status">{avisoAcao}</p>}

          {/* Rodapé do mockup: destrutiva à esquerda, download à direita. */}
          <div className="co-acoes co-acoes-separadas">
            <div className="co-filtros-grupo">
              {info.revalidar && (
                <button type="button" className="co-btn co-btn-primario" disabled={processando} onClick={reenviar}>
                  Reenviar
                </button>
              )}
              {info.podeCancelar && (
                <button type="button" className="co-btn co-btn-secundario" disabled={processando} onClick={() => setDialogCancelar(true)}>
                  Cancelar ATF
                </button>
              )}
              {info.podeExcluir && (
                <button type="button" className="co-btn co-btn-secundario" disabled={processando} onClick={() => setDialogExcluir(true)}>
                  Excluir
                </button>
              )}
            </div>
            {item.status === 'EMITIDA' && (
              <a className="co-btn co-btn-secundario" href={`/api/cooperado/atf/${item.id}/pdf`} target="_blank" rel="noreferrer">
                Baixar PDF
              </a>
            )}
          </div>
        </div>
      )}

      {dialogExcluir && (
        <ModalDecisao
          titulo={`Excluir ${nomeDaAtf}?`}
          contexto={`${item.placa} · ${dataDoDia(item.dataIda)}`}
          consequencia={temDocFiscal
            ? 'O documento fiscal vinculado a esta ATF também é cancelado. Não dá para desfazer.'
            : 'A ATF sai da sua lista. Não dá para desfazer.'}
          rotuloConfirmar="Excluir"
          perigo
          ocupado={processando}
          erro={erroJanela}
          apoio={null}
          onConfirmar={excluir}
          onFechar={fecharJanelas}
        />
      )}

      {dialogCancelar && (
        <ModalDecisao
          titulo={`Cancelar ${nomeDaAtf}?`}
          contexto={`${item.placa} · ${dataDoDia(item.dataIda)}`}
          consequencia="O cancelamento vai ao portal do DER e não dá para desfazer. O documento fiscal da viagem é cancelado junto."
          campos={[
            { chave: 'motivo', rotulo: 'Motivo', tipo: 'selecao', opcoes: motivos.map(m => m.rotulo), obrigatorio: true },
            { chave: 'observacao', rotulo: 'Observação', tipo: 'textarea', maximo: 150 },
          ]}
          rotuloConfirmar="Cancelar a ATF"
          perigo
          ocupado={processando}
          erro={erroJanela}
          apoio={null}
          onConfirmar={cancelar}
          onFechar={fecharJanelas}
        />
      )}

      <GrupoPassageirosDialog
        aberto={dialogGrupo}
        passageiros={item.passageiros.map(p => ({
          nome: p.nome, docIdentificacao: p.docIdentificacao, orgaoExpedidor: p.orgaoExpedidor,
        }))}
        nomeSugerido={item.numeroAtf ? `Grupo da ATF ${item.numeroAtf}` : ''}
        onFechar={() => setDialogGrupo(false)}
        onSalvo={() => onMudou()}
      />
    </article>
  );
}

function ParInfo({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div>
      <dt className="co-rotulo">{rotulo}</dt>
      <dd>{valor}</dd>
    </div>
  );
}
